import { useState } from "react";
import { allOnPageSelected, deselectAllIds, selectAllIds, toggleId } from "../../../utils/selectionManager";
import { EmployeeOverviewListItem } from "../models/employeeOverview.model";

/**
 * Bulk-selection state for Employee Overview (FS_04_EmployeeHub_06 §7). `selectedIds` is the
 * generic Set<number> selection primitive (EmployeeId-keyed, mirroring CVList's `selectedIds`
 * exactly). `selectedPersonIds` is captured alongside it — a per-selected-row EmployeeId -> PersonId
 * map, updated in lockstep — so a future CV-export action can filter the selection down to
 * CV-eligible rows without re-fetching (FS §7.4); it does not change how selection itself works.
 */
export function useEmployeeSelection() {
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [selectedPersonIds, setSelectedPersonIds] = useState<Record<number, number | null>>({});

    const handleEnterSelectionMode = () => {
        setSelectionMode(true);
        setSelectedIds(new Set());
        setSelectedPersonIds({});
    };

    const handleExitSelectionMode = () => {
        setSelectionMode(false);
        setSelectedIds(new Set());
        setSelectedPersonIds({});
    };

    const handleToggleSelect = (employee: EmployeeOverviewListItem) => {
        const isCurrentlySelected = selectedIds.has(employee.employeeId);

        setSelectedIds((prev) => toggleId(prev, employee.employeeId));
        setSelectedPersonIds((prev) => {
            if (isCurrentlySelected) {
                const next = { ...prev };
                delete next[employee.employeeId];
                return next;
            }
            return { ...prev, [employee.employeeId]: employee.personId };
        });
    };

    const handleSelectAllOnPage = (pageEmployees: EmployeeOverviewListItem[]) => {
        const pageIds = pageEmployees.map((e) => e.employeeId);
        setSelectedIds((prev) => selectAllIds(prev, pageIds));
        setSelectedPersonIds((prev) => {
            const next = { ...prev };
            pageEmployees.forEach((e) => {
                next[e.employeeId] = e.personId;
            });
            return next;
        });
    };

    const handleDeselectAllOnPage = (pageEmployees: EmployeeOverviewListItem[]) => {
        const pageIds = pageEmployees.map((e) => e.employeeId);
        setSelectedIds((prev) => deselectAllIds(prev, pageIds));
        setSelectedPersonIds((prev) => {
            const next = { ...prev };
            pageIds.forEach((id) => {
                delete next[id];
            });
            return next;
        });
    };

    const handleClearAll = () => {
        setSelectedIds(new Set());
        setSelectedPersonIds({});
    };

    return {
        selectionMode,
        selectedIds,
        selectedPersonIds,
        allOnPageSelected,
        handleEnterSelectionMode,
        handleExitSelectionMode,
        handleToggleSelect,
        handleSelectAllOnPage,
        handleDeselectAllOnPage,
        handleClearAll,
    };
}
